import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { trackVisit } from "@/lib/api";

import Home from "@/pages/marketing/Home";
import HowItWorks from "@/pages/marketing/HowItWorks";
import Contact from "@/pages/marketing/Contact";
import Pricing from "@/pages/marketing/Pricing";
import Resources from "@/pages/marketing/Resources";
import ProductPage from "@/pages/marketing/ProductPage";
import Article from "@/pages/marketing/Article";
import Courses from "@/pages/marketing/Courses";
import AcademicOverview from "@/pages/admin/AcademicOverview";
import StudentRecords from "@/pages/admin/StudentRecords";
import NotFound from "@/pages/marketing/NotFound";
import StudentProfile from "@/pages/admin/StudentProfile";
import Cohorts from "@/pages/admin/Cohorts";
import CoursePage from "@/pages/marketing/CoursePage";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import PaymentSuccess from "@/pages/payment/Success";
import EnrollReview from "@/pages/marketing/EnrollReview";
import PaymentCancel from "@/pages/payment/Cancel";

import PortalLayout from "@/pages/portal/PortalLayout";
import Dashboard from "@/pages/portal/Dashboard";
import Certificate from "@/pages/portal/Certificate";
import Profile from "@/pages/portal/Profile";
import Intake from "@/pages/portal/Intake";
import MyClarity from "@/pages/portal/MyClarity";
import MyBlueprint from "@/pages/portal/MyBlueprint";
import BlueprintIntake from "@/pages/portal/BlueprintIntake";
import ReportView from "@/pages/portal/ReportView";
import BlueprintView from "@/pages/portal/BlueprintView";
import Messages from "@/pages/portal/Messages";
import Progress from "@/pages/portal/Progress";
import Guided from "@/pages/portal/Guided";
import InHome from "@/pages/portal/InHome";
import Purchases from "@/pages/portal/Purchases";
import Account from "@/pages/portal/Account";
import Timeline from "@/pages/portal/Timeline";
import Modules from "@/pages/portal/Modules";

import OperatorDashboard from "@/pages/operator/OperatorDashboard";
import OperatorCustomer from "@/pages/operator/OperatorCustomer";
import OperatorClients from "@/pages/operator/OperatorClients";
import OperatorOrders from "@/pages/operator/OperatorOrders";
import OperatorInbox from "@/pages/operator/OperatorInbox";
import OperatorProfile from "@/pages/operator/OperatorProfile";

import AdminLayout from "@/pages/admin/AdminLayout";
import Health from "@/pages/admin/Health";
import Ledger from "@/pages/admin/Ledger";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOperators from "@/pages/admin/AdminOperators";
import Experiments from "@/pages/admin/Experiments";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminContent from "@/pages/admin/AdminContent";
import AdminLeads from "@/pages/admin/AdminLeads";
import Customers from "@/pages/admin/Customers";

import OwnerHome from "@/pages/owner/OwnerHome";
import OwnerTest from "@/pages/owner/OwnerTest";
import IntakeManager from "@/pages/owner/IntakeManager";
import TestModeBanner from "@/components/TestModeBanner";

function ScrollAndTrack() {
  const loc = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    const isPrivate = ["/portal", "/operator", "/admin", "/owner"].some((p) => loc.pathname.startsWith(p));
    if (!isPrivate) {
      trackVisit(loc.pathname);
      // GA4 SPA pageview — pathname only, no query strings or customer/intake data.
      // Skip the very first route: the inline loader in index.html already sent the initial page_view.
      if (!firstLoad.current && window.gtag) {
        window.gtag("event", "page_view", {
          page_path: loc.pathname,
          page_location: window.iscAdLocation ? window.iscAdLocation(loc.pathname) : window.location.origin + loc.pathname,
          page_title: document.title,
        });
      }
    }
    firstLoad.current = false;
  }, [loc.pathname]);
  return null;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-[#6B665E]">Loading…</div>;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollAndTrack />
          <TestModeBanner />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/infant-sleep-consultant-near-me" element={<Navigate to="/" replace />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:slug" element={<CoursePage />} />

            <Route path="/infant-sleep-diagnostic" element={<ProductPage productKey="diagnostic" />} />
            <Route path="/personalized-infant-sleep-blueprint" element={<ProductPage productKey="blueprint" />} />
            <Route path="/guided-infant-sleep-consulting" element={<ProductPage productKey="guided" />} />
            <Route path="/in-home-infant-sleep-consulting" element={<ProductPage productKey="inhome_overnight" />} />
            <Route path="/overnight-infant-sleep-consultant" element={<Navigate to="/in-home-infant-sleep-consulting" replace />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/reset-password" element={<Reset />} />
            <Route path="/enroll/:product" element={<EnrollReview />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/certificate/:slug" element={<Protected roles={["customer"]}><Certificate /></Protected>} />
            <Route path="/portal" element={<Protected roles={["customer"]}><PortalLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="clarity" element={<MyClarity />} />
              <Route path="my-blueprint" element={<MyBlueprint />} />
              <Route path="diagnostic" element={<Intake />} />
              <Route path="blueprint-intake" element={<BlueprintIntake />} />
              <Route path="report" element={<ReportView />} />
              <Route path="blueprint" element={<BlueprintView />} />
              <Route path="progress" element={<Progress />} />
              <Route path="messages" element={<Messages />} />
              <Route path="guided" element={<Guided />} />
              <Route path="inhome" element={<InHome />} />
              <Route path="purchases" element={<Purchases />} />
              <Route path="account" element={<Account />} />
              <Route path="timeline" element={<Timeline />} />
              <Route path="resources" element={<Modules />} />
            </Route>

            <Route path="/operator" element={<Protected roles={["operator", "admin"]}><OperatorDashboard /></Protected>} />
            <Route path="/operator/clients" element={<Protected roles={["operator", "admin"]}><OperatorClients /></Protected>} />
            <Route path="/operator/inbox" element={<Protected roles={["operator", "admin"]}><OperatorInbox /></Protected>} />
            <Route path="/operator/profile" element={<Protected roles={["operator", "admin"]}><OperatorProfile /></Protected>} />
            <Route path="/operator/orders" element={<Protected roles={["operator", "admin"]}><OperatorOrders /></Protected>} />
            <Route path="/operator/customer/:id" element={<Protected roles={["operator", "admin"]}><OperatorCustomer /></Protected>} />

            <Route path="/admin" element={<Protected roles={["admin"]}><AdminLayout /></Protected>}>
              <Route index element={<AcademicOverview />} />
              <Route path="health" element={<Health />} />
              <Route path="students" element={<StudentRecords />} />
              <Route path="students/:id" element={<StudentProfile />} />
              <Route path="cohorts" element={<Cohorts />} />
              <Route path="ledger" element={<Ledger />} />
              <Route path="customers" element={<Customers />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="operators" element={<AdminOperators />} />
              <Route path="experiments" element={<Experiments />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="leads" element={<AdminLeads />} />
            </Route>

            <Route path="/owner" element={<Protected roles={["admin"]}><OwnerHome /></Protected>} />
            <Route path="/owner/test" element={<Protected roles={["admin"]}><OwnerTest /></Protected>} />
            <Route path="/owner/intake-manager" element={<Protected roles={["admin"]}><IntakeManager /></Protected>} />

            <Route path="/:slug" element={<Article />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
